import { generateCSVContent } from '@airreading/common';
import type { CoachCsvApplySummary } from '@airreading/common/data';

import { CsvColumn, displayCsvUploadRow, downloadCSV } from './csv-utils';

type CoachCsvSummaryExportRow = {
  outcome: string;
  action: string;
  row: number;
  email: string;
  displayName: string;
  details: string;
};

const SUMMARY_COLUMNS: CsvColumn<CoachCsvSummaryExportRow>[] = [
  { id: 'Outcome', getValue: (row) => row.outcome },
  { id: 'Action', getValue: (row) => row.action },
  { id: 'Row', getValue: (row) => displayCsvUploadRow(row.row) },
  { id: 'Email', getValue: (row) => row.email },
  { id: 'Display name', getValue: (row) => row.displayName || null },
  { id: 'Details', getValue: (row) => row.details || null },
];

function formatCreateDetails(values: Record<string, string | null>): string {
  const entries = Object.entries(values).filter(([, value]) => value != null && value !== '');
  if (entries.length === 0) {
    return '';
  }

  return JSON.stringify(Object.fromEntries(entries));
}

function formatUpdateDetails(changes: CoachCsvApplySummary['updates'][number]['changes']): string {
  if (changes.length === 0) {
    return '';
  }

  return JSON.stringify(
    Object.fromEntries(changes.map((change) => [change.field, { current: change.current, proposed: change.proposed }])),
  );
}

function summaryToExportRows(summary: CoachCsvApplySummary): CoachCsvSummaryExportRow[] {
  return [
    ...summary.creates.map((delta) => ({
      outcome: 'Success',
      action: 'Create',
      row: delta.row,
      email: delta.email,
      displayName: delta.displayName,
      details: formatCreateDetails(delta.values),
    })),
    ...summary.updates.map((delta) => ({
      outcome: 'Success',
      action: 'Update',
      row: delta.row,
      email: delta.email,
      displayName: '',
      details: formatUpdateDetails(delta.changes),
    })),
    ...summary.failures.map((failure) => ({
      outcome: 'Failure',
      action: 'Fail',
      row: failure.row,
      email: failure.email ?? '',
      displayName: '',
      details: failure.message,
    })),
    ...summary.skips.map((skip) => ({
      outcome: 'Skip',
      action: 'Skip',
      row: skip.row,
      email: skip.email,
      displayName: '',
      details: skip.reason,
    })),
  ];
}

/** Downloads a flat CSV of create/update/fail/skip rows from a teacher CSV dry-run or apply summary. */
export function exportCoachCsvSummaryToCSV(summary: CoachCsvApplySummary, filename: string): void {
  const rows = summaryToExportRows(summary);
  if (rows.length === 0) {
    downloadCSV('Outcome,Action,Row,Email,Display name,Details\n', filename);
    return;
  }

  downloadCSV(generateCSVContent(rows, SUMMARY_COLUMNS), filename);
}
